import { IIcon, ITopBar } from '@components/std-components/TopBar'
import { Divider } from '@material-ui/core'
import { TableSettingsIcon } from '@resources/images'
import '@styles/HierarchyWiseTable/style.css'
import inRange from 'lodash/inRange'
import concat from 'lodash/concat'
import { useObserver } from 'mobx-react-lite'
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react'
import { HierarchyWiseItems, HierarchyWiseItemsProps } from '../HierarchyWiseItems'
import HierarchyItemsContext from '../HierarchyWiseItems/contexts/hierarchy-items-context'
import RendererFactory from './components/Renderer'
import SettingCmp from './components/Settings'
import RendererContext from './contexts/renderer-context'
import useHierarchyItemTableContext from './hooks/use-hierarchy-item-table-context'
import { Column } from './models/column'
import { Settings, SettingState } from './models/settings'
import HierarchyWiseItemsTableStore from './stores/HierarchyWiseItemsTableStore'
import { useTranslation } from 'react-i18next'
import useOrderContext from '@modules/order/hooks/use-order-context'
import { KPITableView } from '@modules/order/stores/view/KPI/KPIViewTableService'
import { stores } from '@stores'
import { useHierarchyWiseItemsTableStyle } from './HierarchyWiseItemsTableStyle'
import { enhancedTableSettingsContext } from '@modules/showroom/EnhancedTableSettings/context/enhancedTableSettings-context'
import { TABLE_SETTING_TYPE } from '@modules/showroom/EnhancedTableSettings/store/EnhancedTableSettingsStore'

export interface EnhancedHierarchyWiseItemsTableProps
  extends Omit<HierarchyWiseItemsProps, 'Renderer' | 'store'>,
    Pick<ITopBar, 'rightIcons' | 'showRightIcons'> {
  columns: Column[]
  headerRows?: any[]
  settings?: Settings
  hierarchyRowElement?: React.ElementType
  showToolbar?: boolean
  showBorders?: boolean
  onSettingsChange?: (settings: SettingState) => void
  translations?: any
  translationKey?: string
  actions?: IIcon[]
  selectedView?: any
  tableStoreInstance?: any
  tableContextValue?: any
}

export interface EnhancedHierarchyWiseItemsTableRef {
  tableRef: {
    scrollToColumn: (colIndex: number) => void
    getColumnByVisibleIndex: (visibleColIndex: number) => Column
    isColumnVisible: (visibleColIndex: number) => boolean
    columns: Column[]
    visibleColumns: Column[]
    flattenedVisibleColumns: Column[]
    headerRows?: any[]
  }
}

const EnhancedHierarchyWiseItemsTable = forwardRef<
  EnhancedHierarchyWiseItemsTableRef,
  EnhancedHierarchyWiseItemsTableProps
>(
  (
    {
      hierarchyRowElement,
      columns,
      headerRows = [],
      showToolbar = true,
      settings,
      showBorders = true,
      actions,
      translationKey,
      onSettingsChange,
      rightIcons = [],
      tableStoreInstance,
      tableContextValue,
      ...props
    },
    ref
  ) =>
    useObserver(function useHook() {
      const { isVisible: isSettingVisible, element: SettingElement = SettingCmp } =
        settings || ({} as Settings)
      const tableStore = useMemo(() => {
        if (!tableStoreInstance) {
          const temp = new HierarchyWiseItemsTableStore()
          return temp
        }
        return tableStoreInstance
      }, [])

      const tableSettingsStore = useContext(enhancedTableSettingsContext)
      const {
        uiStore: { setSettings, setTableSettingsData, isTableSettingActive }
      } = tableSettingsStore

      const {
        columnOffsetWidthByVisibleIndex,
        getColumnByVisibleIndex,
        columnMetaByVisibleIndex,
        flattenedVisibleColumns,
        filteredColumns,
        doorKipHeader
      } = tableStore

      const orderContext = useOrderContext()
      const tableSetting = orderContext?.uiStore?.dataStore?.tableSetting

      const { store } = useHierarchyItemTableContext()
      const { t } = useTranslation()
      const classes = useHierarchyWiseItemsTableStyle()

      const rendererContextValue = useMemo(
        () => ({
          tableContext: tableContextValue,
          root: { id: props.rootId, height: props.rootHeight },
          showBorders,
          width: props.dimensions?.container?.width
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
          tableContextValue,
          props?.rootId,
          props?.rootHeight,
          showBorders,
          props.dimensions?.container?.width
        ]
      )
      const toolbarRightIcons = useMemo(
        () =>
          showToolbar
            ? concat(
                [...rightIcons],
                isSettingVisible
                  ? ({
                      showIcon: true,
                      ...(settings?.element
                        ? {
                            isCustomElement: true,
                            customElement: SettingElement,
                            customElementProps: {
                              settings: tableStore.settings,
                              onSettingsChange: updatedSettings => {
                                tableStore.updateSettings(updatedSettings)
                                onSettingsChange?.(updatedSettings)
                              }
                            }
                          }
                        : {
                            isPopUp: true,
                            popUpContent: (
                              <SettingElement
                                settings={tableStore.settings}
                                onSettingsChange={updatedSettings => {
                                  tableStore.updateSettings(updatedSettings)
                                  onSettingsChange?.(updatedSettings)
                                }}
                              />
                            ),
                            popUpProps: {
                              position: 'bottom right'
                            },
                            name: 'tableSettings',
                            text: t('order.tableSettings', 'Table Settings'),
                            image: TableSettingsIcon,
                            typographyDetails: {
                              variant: 'captionDarkGrey'
                            }
                          })
                    } as IIcon)
                  : [],
                ...actions
              )
            : [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [showToolbar, tableStore.settings, actions, rightIcons]
      )
      useEffect(() => {
        tableStore.setColumns(columns, t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [columns])

      useEffect(() => {
        setSettings(tableStore?.settings ?? {})
        setTableSettingsData(columns, headerRows || [], props?.selectedView ?? '', t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [columns, tableStore?.settings])

      useEffect(() => {
        !isTableSettingActive &&
          setTableSettingsData(columns, headerRows || [], props?.selectedView ?? '', t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [isTableSettingActive])

      useEffect(() => {
        if (tableSetting && props?.selectedView) {
          let setting = tableSetting[props.selectedView] || {}
          if (props.selectedView === KPITableView.DoorsKPI) {
            setting.columns = {
              ...tableSetting[TABLE_SETTING_TYPE.DoorKpiHeader]?.columns,
              ...tableSetting[TABLE_SETTING_TYPE.DoorKpiSubHeader]?.columns
            }
          }
          tableStore.updateSettings(setting || {})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [tableSetting, doorKipHeader])

      const itemsRef = useRef()
      useImperativeHandle(
        ref,
        () => ({
          tableRef: {
            scrollToColumn: (colIndex: number) => {
              const offsetWidth = columnOffsetWidthByVisibleIndex(colIndex)
              const width = Math.ceil(store.elementRefStore.ref.elem.clientWidth / 2)
              const scrollWidth = store.elementRefStore.ref.elem.scrollWidth
              let left = offsetWidth
              if (store.elementRefStore.ref.elem.scrollLeft === 0) {
                left = offsetWidth + width
              }
              if (
                store.elementRefStore.ref.elem.scrollLeft +
                  store.elementRefStore.ref.elem.clientWidth ===
                scrollWidth
              ) {
                left = left - width
              }
              store.elementRefStore.ref.elem.scrollLeft = left
            },
            isColumnVisible: (colIndex: number) => {
              const offsetWidth = columnOffsetWidthByVisibleIndex(colIndex)
              const meta = columnMetaByVisibleIndex(colIndex)
              return (
                inRange(
                  offsetWidth,
                  store.elementRefStore.ref.elem.scrollLeft,
                  store.elementRefStore.ref.elem.clientWidth
                ) &&
                inRange(
                  offsetWidth + meta.width,
                  store.elementRefStore.ref.elem.scrollLeft,
                  store.elementRefStore.ref.elem.clientWidth
                )
              )
            },
            getColumnByVisibleIndex,
            columns,
            flattenedVisibleColumns,
            visibleColumns: filteredColumns,
            headerRows
          },
          ref: itemsRef.current
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
          itemsRef,
          columns,
          flattenedVisibleColumns,
          filteredColumns,
          store?.elementRefStore?.ref?.elem
        ]
      )

      const {
        productConfiguration: { isEnhancedBasketView }
      } = stores
      const hideRootDivider = isEnhancedBasketView

      return (
        <div className='hierarchy-wise-table-container'>
          <HierarchyItemsContext.Provider value={{ store }}>
            <RendererContext.Provider value={rendererContextValue}>
              {toolbarRightIcons?.length ? (
                <div className={classes.hierarchyWiseTableActionContainer}>
                  <div style={{ height: 8 }} />
                </div>
              ) : null}
              {hideRootDivider ? null : (
                <Divider orientation='horizontal' style={{ width: '100%' }} />
              )}
              <HierarchyWiseItems
                Renderer={RendererFactory}
                {...props}
                pageSize={2}
                ref={itemsRef}
              />
            </RendererContext.Provider>
          </HierarchyItemsContext.Provider>
        </div>
      )
    })
)

EnhancedHierarchyWiseItemsTable.displayName = 'HierarchyWiseItemsTable'

export default EnhancedHierarchyWiseItemsTable
